import React from "react";
import { Avatar, Card, Chip, Kicker, SectionHead } from "@/components/kit";
import { memberScore, milestonesHit, reportStreak, tierFor, currentPct } from "@/lib/util";
import { Flame, Medal, Trophy } from "lucide-react";

/* Momentum ring — a member's average goal progress as a brass dial. */
export const MomentumRing = ({ value = 0, size = 58, label, testId }) => {
  const r = (size - 7) / 2;
  const c = 2 * Math.PI * r;
  return (
    <span className="relative inline-flex items-center justify-center shrink-0" data-testid={testId}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(15,23,42,.08)" strokeWidth="4" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--brass-bright)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (Math.max(0, Math.min(100, value)) / 100) * c}
          style={{ transition: "stroke-dashoffset .8s var(--ease)" }}
        />
      </svg>
      <span className="absolute numeral" style={{ fontSize: size * 0.28 }}>
        {Math.round(value)}
        {label !== false && <span style={{ fontSize: size * 0.16 }}>%</span>}
      </span>
    </span>
  );
};

export const StreakChip = ({ n, testId }) =>
  n > 1 ? (
    <span className="chip" data-testid={testId} style={{ color: "var(--rust)", borderColor: "rgba(156,79,50,.4)", background: "rgba(156,79,50,.08)" }}>
      <Flame size={11} /> {n} chapter streak
    </span>
  ) : null;

export const Badges = ({ goal, testId }) => {
  const hit = milestonesHit(goal);
  if (!hit.length) return null;
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={testId}>
      {hit.map((m) => (
        <span
          key={m}
          title={`${m}% milestone`}
          className="serif inline-flex items-center justify-center rounded-full"
          style={{
            width: 26,
            height: 26,
            fontSize: 10,
            color: m === 100 ? "#FFFFFF" : "var(--brass)",
            background: m === 100 ? "var(--brass)" : "rgba(37,99,235,.10)",
            border: "1px solid rgba(37,99,235,.45)",
          }}
        >
          {m}
        </span>
      ))}
    </div>
  );
};

/* Standings — points earned for reporting, gaining ground and finishing. */
export const Standings = ({ members, goals, meId }) => {
  const rows = members
    .map((m) => ({ m, ...memberScore(goals.filter((g) => g.memberId === m.id)) }))
    .sort((a, b) => b.points - a.points);
  const top = rows[0]?.points || 1;

  return (
    <Card data-testid="standings">
      <SectionHead
        right={
          <span className="kicker !text-[8px]" style={{ color: "var(--ink-mute)" }}>
            Reporting · gains · milestones
          </span>
        }
      >
        Standings
      </SectionHead>
      <div className="divide-hair">
        {rows.map((r, i) => (
          <div
            key={r.m.id}
            data-testid={`standing-${r.m.id}`}
            className="flex items-center gap-4 py-3.5"
            style={{ background: r.m.id === meId ? "rgba(37,99,235,.06)" : "transparent" }}
          >
            <span
              className="numeral w-6 text-center shrink-0"
              style={{ color: i < 3 ? "var(--brass)" : "var(--ink-mute)", fontSize: 18 }}
            >
              {i + 1}
            </span>
            <Avatar name={r.m.name} color={r.m.avatarColor} size={32} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm truncate">{r.m.name}</span>
                {i === 0 && (
                  <Trophy size={12} style={{ color: "var(--brass-bright)" }} title="Leading" />
                )}
                {r.completed > 0 && (
                  <span className="chip !py-0.5" style={{ color: "var(--green)", borderColor: "rgba(63,107,79,.4)", background: "rgba(63,107,79,.08)" }}>
                    <Medal size={10} /> {r.completed} done
                  </span>
                )}
              </div>
              <div className="mt-2 h-[5px] rounded-full overflow-hidden" style={{ background: "rgba(15,23,42,.08)" }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.round((r.points / top) * 100)}%`,
                    background: "linear-gradient(90deg, rgba(37,99,235,.75), var(--brass))",
                    transition: "width .7s var(--ease)",
                  }}
                />
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="numeral text-lg leading-none" data-testid={`points-${r.m.id}`}>
                {r.points}
              </div>
              <div className="kicker !text-[8px] mt-1">{tierFor(r.points)}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export const MemberMomentum = ({ member, goals }) => {
  const s = memberScore(goals);
  const streak = goals.length ? Math.max(...goals.map(reportStreak)) : 0;
  return (
    <div className="flex items-center gap-4" data-testid={`momentum-${member.id}`}>
      <MomentumRing value={s.momentum} testId={`momentum-ring-${member.id}`} />
      <div>
        <div className="numeral text-sm">{s.points} pts</div>
        <div className="kicker !text-[8px] mt-1">{tierFor(s.points)}</div>
      </div>
      <StreakChip n={streak} testId={`streak-${member.id}`} />
    </div>
  );
};
